import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { MdDateRange } from "react-icons/md";
import { RiCloseLargeFill } from "react-icons/ri";
import toast from "react-hot-toast";
import Loader from "../../../components/Loader";
import ReportManageServices from "../../../services/ReportManageServices";
import "../../../styles/ReportDateDetailModal.css";

const api = new ReportManageServices();

const fmt = (n) => Number(n ?? 0).toLocaleString("en-IN");

const fmtPL = (v) => {
  const n = Number(v ?? 0);
  if (n >= 0) return fmt(n);
  return `-${fmt(Math.abs(n))}`;
};

const plClass = (v) => (Number(v ?? 0) >= 0 ? "rdd-pl-pos" : "rdd-pl-neg");

function GameMetricCells({ purchase, win, cancel, pl }) {
  return (
    <>
      <td className="rdd-num">{fmt(purchase)}</td>
      <td className="rdd-num">{fmt(win)}</td>
      <td className="rdd-num">{fmt(cancel)}</td>
      <td className={`rdd-num rdd-pl ${plClass(pl)}`}>{fmtPL(pl)}</td>
    </>
  );
}

const userHasPurchase = (user) =>
  (user?.games || []).some((g) => Number(g.purchase || 0) > 0);

const TABLE_COLS = 6;

function UserGameRows({ user, variant = "outlet" }) {
  const label = user.username || user.user_name || `User #${user.user_id}`;
  const gameRows = [];

  // One row per game the user touched that day; a game with nothing in it
  // is left out so a player who only plays one game gets one line.
  if (userHasPurchase(user)) {
    for (const g of user.games || []) {
      if (!Number(g.purchase || 0) && !Number(g.win || 0) && !Number(g.cancel || 0))
        continue;
      gameRows.push({
        type: g.game_code,
        label: g.game_name || g.game_code,
        purchase: g.purchase,
        win: g.win,
        cancel: g.cancel,
        pl: g.profit_loss,
      });
    }
  }

  if (gameRows.length === 0) return null;

  return (
    <tbody className="rdd-user-group">
      {gameRows.map((row, idx) => (
        <tr key={`${user.user_id}-${row.type}`} className="rdd-type-row">
          {idx === 0 && (
            <td
              rowSpan={gameRows.length}
              className={`rdd-user-cell rdd-user-cell-${variant}`}
            >
              <span className="rdd-user-cell-inner">
                <span className="rdd-user-dot" />
                <span className="rdd-user-name">{label}</span>
              </span>
            </td>
          )}
          <td className="rdd-game-type">{row.label}</td>
          <GameMetricCells
            purchase={row.purchase}
            win={row.win}
            cancel={row.cancel}
            pl={row.pl}
          />
        </tr>
      ))}
    </tbody>
  );
}

function GameSummaryTable({ summary }) {
  const games = summary.games || [];
  const total = summary.total_data || {};
  return (
    <div className="rdd-breakdown rdd-summary">
      <div className="rdd-breakdown-title">Game summary</div>
      <div className="rdd-table-scroll">
        <table className="rdd-table rdd-summary-table">
          <thead>
            <tr>
              <th>Game</th>
              <th>Purchase</th>
              <th>Win</th>
              <th>Cancel</th>
              <th>P/L</th>
            </tr>
          </thead>
          <tbody>
            {games.map((g) => (
              <tr key={g.game_code} className="rdd-summary-row">
                <td className="rdd-summary-game">{g.game_name || g.game_code}</td>
                <GameMetricCells
                  purchase={g.total_purchase_amount}
                  win={g.total_win_amount}
                  cancel={g.total_cancel_amount}
                  pl={g.profit_loss}
                />
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="rdd-grand-total">
              <td>Total</td>
              <GameMetricCells
                purchase={total.total_purchase_amount}
                win={total.total_win_amount}
                cancel={total.total_cancel_amount}
                pl={total.overall_profit_loss}
              />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

const ReportDateDetailModal = ({
  show,
  onClose,
  txnDate,
  userId,
  displayUserName,
  loggedInRoleId,
}) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!show || !txnDate) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setDetail(null);
      try {
        const res = await api.getReportDateDetail(txnDate, userId);
        if (cancelled) return;
        if (res?.isSuccess) {
          setDetail(res.data);
        } else {
          setDetail(null);
          toast.error(res?.message || "Failed to load day detail");
        }
      } catch {
        if (!cancelled) {
          setDetail(null);
          toast.error("Failed to load day detail");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, txnDate, userId]);

  const summary = detail?.summary;
  const titleName = detail?.target_user_name || displayUserName || "—";
  const hideSuperAdmin = Number(loggedInRoleId) === 1;

  const activeOutletUsers = useMemo(
    () => (detail?.outlet_users || []).filter(userHasPurchase),
    [detail?.outlet_users],
  );
  const activeMobileUsers = useMemo(
    () => (detail?.mobile_users || []).filter(userHasPurchase),
    [detail?.mobile_users],
  );
  // Each sub admin is one row: their whole branch (outlets + players)
  // rolled up per game. Their users are not repeated in the other sections.
  const activeSubAdminUsers = useMemo(
    () => (detail?.sub_admin_users || []).filter(userHasPurchase),
    [detail?.sub_admin_users],
  );
  const hasBreakdown =
    activeOutletUsers.length > 0 ||
    activeMobileUsers.length > 0 ||
    activeSubAdminUsers.length > 0;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="xl"
      className="rdd-modal"
      scrollable
    >
      <div className="rdd-modal-head">
        <div className="rdd-modal-head-inner">
          <MdDateRange size={22} />
          <div>
            <h2 className="rdd-modal-title">
              {txnDate ? moment(txnDate).format("DD MMM YYYY") : "—"}
            </h2>
            <p className="rdd-modal-sub">[{titleName}]</p>
          </div>
        </div>
        <button
          type="button"
          className="rdd-close"
          onClick={onClose}
          aria-label="Close"
        >
          <RiCloseLargeFill size={18} />
        </button>
      </div>

      <Modal.Body className="rdd-modal-body">
        {loading ? (
          <Loader message="Loading day detail…" />
        ) : !detail ? (
          <p className="rdd-empty">No detail available for this date.</p>
        ) : (
          <>
            {summary && (
              <>
                <div className="rdd-balance-bar">
                  <span>
                    Opening Balance:{" "}
                    <strong>{fmt(summary.user_opening_balance)}</strong>
                  </span>
                  <span>
                    Closing Balance:{" "}
                    <strong>{fmt(summary.user_closing_balance)}</strong>
                  </span>
                </div>
                <GameSummaryTable summary={summary} />
              </>
            )}

            {detail.is_admin_hierarchy && !hasBreakdown && summary && (
              <p className="rdd-no-activity">
                No outlet or mobile users with game purchase on this date.
              </p>
            )}

            {hasBreakdown && (
              <div className="rdd-breakdown">
                <div className="rdd-breakdown-title">
                  User-wise game breakdown
                </div>
                <div className="rdd-table-scroll">
                  <table className="rdd-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Type</th>
                        <th>Purchase</th>
                        <th>Win</th>
                        <th>Cancel</th>
                        <th>P/L</th>
                      </tr>
                    </thead>
                    {activeSubAdminUsers.length > 0 && (
                      <>
                        <tbody>
                          <tr className="rdd-section-row rdd-section-subadmin">
                            <td colSpan={TABLE_COLS}>Sub admin users</td>
                          </tr>
                        </tbody>
                        {activeSubAdminUsers.map((u) => (
                          <UserGameRows
                            key={`s-${u.user_id}`}
                            user={u}
                            variant="subadmin"
                          />
                        ))}
                      </>
                    )}
                    {activeOutletUsers.length > 0 && (
                      <>
                        <tbody>
                          <tr className="rdd-section-row">
                            <td colSpan={TABLE_COLS}>Outlet users</td>
                          </tr>
                        </tbody>
                        {activeOutletUsers.map((u) => (
                          <UserGameRows
                            key={`o-${u.user_id}`}
                            user={u}
                            variant="outlet"
                          />
                        ))}
                      </>
                    )}
                    {activeMobileUsers.length > 0 && (
                      <>
                        <tbody>
                          <tr className="rdd-section-row rdd-section-mobile">
                            <td colSpan={TABLE_COLS}>Mobile users</td>
                          </tr>
                        </tbody>
                        {activeMobileUsers.map((u) => (
                          <UserGameRows
                            key={`m-${u.user_id}`}
                            user={u}
                            variant="mobile"
                          />
                        ))}
                      </>
                    )}
                    <tfoot>
                      <tr className="rdd-grand-total">
                        <td colSpan={2}>Total</td>
                        <td className="rdd-num">
                          {fmt(detail.grand_total?.purchase)}
                        </td>
                        <td className="rdd-num">
                          {fmt(detail.grand_total?.win)}
                        </td>
                        <td className="rdd-num">
                          {fmt(detail.grand_total?.cancel)}
                        </td>
                        <td
                          className={`rdd-num rdd-pl ${plClass(detail.grand_total?.profit_loss)}`}
                        >
                          {fmtPL(detail.grand_total?.profit_loss)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ReportDateDetailModal;
